/**
 * Shared guest-auth paint defense (G4/G5 / G19).
 *
 * Defense in depth for dedicated guest-auth routes: hold paint while session
 * hydrates or OIDC land is pending, and replace-navigate when globally signed in.
 * Mirrors RequireGuestAuth oidcLandingPending SSOT (`window.location.search` +
 * OIDC_LAND_QUERY_CHANGED_EVENT). Inline embeds (non-guest paths) skip the gate.
 *
 * G19 — session authority is CSP memory session after `/me` **200** only.
 * BAN memory JWT / readable access cookie / getToken as signed-in proof
 * under dual|cookie. OIDC land pending = land query only (HttpOnly jar has no
 * readable access JWT). Parity with RequireGuestAuth (half-migration = Fail).
 */

import { useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { useOidcLandGuardPending } from '../../../shared/session/useOidcLandGuardPending.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { evaluateGuestOnlyAuth } from './evaluateGuestOnlyAuth.js';
import { isGuestAuthPath } from './isGuestAuthPath.js';
import { resolveGuestAuthRedirectTarget } from './resolveGuestAuthRedirectTarget.js';
import { clearPostAuthNavigateHandoff } from '../../../shared/session/returnTarget.js';

export interface GuestAuthScreenDefense {
  readonly guestAuthBlocked: boolean;
  readonly t: (key: string) => string;
}

export function useGuestAuthScreenDefense(): GuestAuthScreenDefense {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, sessionHydrated } = useCustomerSession();
  const { oidcLandingPending, oidcLandBudgetExhausted } =
    useOidcLandGuardPending();

  const onGuestAuthRoute = isGuestAuthPath(location.pathname);

  const guestAuthDecision = useMemo(() => {
    if (!onGuestAuthRoute) {
      return evaluateGuestOnlyAuth({
        sessionHydrated: true,
        globallySignedIn: false,
        oidcLandingPending: false,
      });
    }

    // G19 — signed-in = `/me` session DTO only (mirrors RequireGuestAuth).
    const globallySignedIn = session !== null;

    return evaluateGuestOnlyAuth({
      sessionHydrated,
      globallySignedIn,
      oidcLandingPending,
      oidcLandBudgetExhausted,
    });
  }, [
    onGuestAuthRoute,
    oidcLandingPending,
    oidcLandBudgetExhausted,
    session,
    sessionHydrated,
  ]);

  const guestAuthBlocked =
    onGuestAuthRoute && !guestAuthDecision.allowGuestScreen;

  const guestAuthRedirectedRef = useRef(false);

  useLayoutEffect(() => {
    if (!onGuestAuthRoute) {
      // G1 SoftRace — do NOT clear on mere leave. `navigateToPostAuthReturnTarget`
      // owns deferred microtask clear; eager leave-clear races same-tick peek
      // (park → beat /browse). Reset bounce latch only.
      guestAuthRedirectedRef.current = false;
      return;
    }
    if (!guestAuthDecision.redirectAway || guestAuthRedirectedRef.current) {
      return;
    }
    guestAuthRedirectedRef.current = true;
    // Peek-based resolve first (SoftRace), then navigate — clear-after-win.
    navigate(resolveGuestAuthRedirectTarget(tenantCode), { replace: true });
    // Eager clear bumps generation so a pending scheduleClear is a no-op
    // (parity with RequireGuestAuth signed-in Navigate / returnTarget G1).
    clearPostAuthNavigateHandoff();
  }, [
    onGuestAuthRoute,
    guestAuthDecision.redirectAway,
    navigate,
    tenantCode,
  ]);

  return {
    guestAuthBlocked,
    t,
  };
}
